
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../services/api";
import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaUsers,
  FaChalkboardTeacher,
  FaDoorOpen,
  FaClock,
  FaCalendarAlt,
  FaLayerGroup,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaSave,
  FaBookOpen,
  FaUserGraduate,
  FaGraduationCap,
  FaFilter,
  FaSyncAlt,
  FaCheckCircle,
  FaExclamationCircle,
  FaRegCalendarCheck
} from "react-icons/fa";
import "./Turmas.css";

const initialForm = {
  nivel_id: "",
  sala_id: "",
  professor_id: "",
  horario_id: "",
  data_inicio: "",
  data_fim: "",
  status: "ATIVA",
  inscricoes_ids: []
};

const getError = (error, fallback) =>
  error.response?.data?.msg ||
  error.response?.data?.erro ||
  error.message ||
  fallback;

const formatDate = (date) => {
  if (!date) return "Não definida";

  const value = String(date).slice(0, 10);
  const parts = value.split("-");

  if (parts.length !== 3) return value;

  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const getEnrollmentName = (item) => {
  const nome =
    item.aluno_nome ||
    item.nome_aluno ||
    item.nome_completo ||
    item.nome ||
    item.aluno?.nome ||
    item.aluno?.nome_completo ||
    item.aluno?.aluno_nome;

  return nome ? String(nome).trim() : "Nome não disponível";
};

const getStudentNumber = (item) =>
  item.numero_estudante ||
  item.aluno?.numero_estudante ||
  "Sem número";

const getEnrollmentId = (item) =>
  Number(item.id || item.inscricao_id);

function Turmas() {
  const [turmas, setTurmas] = useState([]);
  const [niveis, setNiveis] = useState([]);
  const [salas, setSalas] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [inscricoes, setInscricoes] = useState([]);

  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState([]);
  const [detalhes, setDetalhes] = useState({});
  const [loadingDetails, setLoadingDetails] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filtroNivel, setFiltroNivel] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("TODAS");
  const [studentSearch, setStudentSearch] = useState("");

  const [notice, setNotice] = useState(null);

  const notify = (type, message) => {
    setNotice({ type, message });

    window.setTimeout(() => {
      setNotice(null);
    }, 4500);
  };

  // ==========================================
  // CARREGAR DADOS
  // ==========================================

  const loadTurmas = useCallback(async () => {
    const response = await api.get("/turmas");
    setTurmas(Array.isArray(response.data) ? response.data : []);
  }, []);

  const loadDados = useCallback(async () => {
    setLoading(true);

    try {
      const resultados = await Promise.allSettled([
        api.get("/turmas"),
        api.get("/niveis"),
        api.get("/salas"),
        api.get("/professores"),
        api.get("/horarios"),
        api.get("/inscricoes")
      ]);

      const nomes = [
        "turmas",
        "niveis",
        "salas",
        "professores",
        "horarios",
        "inscricoes"
      ];

      const setters = [
        setTurmas,
        setNiveis,
        setSalas,
        setProfessores,
        setHorarios,
        setInscricoes
      ];

      resultados.forEach((resultado, index) => {
        if (resultado.status === "fulfilled") {
          const data = resultado.value.data;
          setters[index](Array.isArray(data) ? data : []);
        } else {
          console.error(
            `Erro ao carregar ${nomes[index]}:`,
            resultado.reason
          );
        }
      });

      if (resultados[0].status === "rejected") {
        notify("error", "Não foi possível carregar as turmas.");
      }

    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      notify("error", "Erro ao carregar os dados do módulo.");

    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDados();
  }, [loadDados]);

  // ==========================================
  // DADOS AUXILIARES
  // ==========================================

  const getNivel = (id) =>
    niveis.find((item) => Number(item.id) === Number(id));

  const getSala = (id) =>
    salas.find((item) => Number(item.id) === Number(id));

  const getProfessor = (id) =>
    professores.find((item) => Number(item.id) === Number(id));

  const getHorario = (id) =>
    horarios.find((item) => Number(item.id) === Number(id));

  const getNivelNome = (turma) =>
    turma.nivel_nome ||
    getNivel(turma.nivel_id)?.nome ||
    `Nível ${turma.nivel_id}`;

  const getSalaNome = (turma) =>
    turma.sala_nome ||
    getSala(turma.sala_id)?.nome ||
    "Sala não definida";

  const getProfessorNome = (turma) =>
    turma.professor_nome ||
    getProfessor(turma.professor_id)?.nome ||
    "Professor não atribuído";

  const getHorarioTexto = (turma) => {
    const horario = getHorario(turma.horario_id);

    const inicio = turma.hora_inicio || horario?.hora_inicio;
    const fim = turma.hora_fim || horario?.hora_fim;

    if (!inicio || !fim) return "Horário não definido";

    return `${String(inicio).slice(0, 5)} - ${String(fim).slice(0, 5)}`;
  };

  const getTotalAlunos = (turma) =>
    Number(
      detalhes[turma.id]?.total_alunos ??
      turma.total_alunos ??
      0
    );

  // ==========================================
  // AGRUPAMENTO POR NÍVEL
  // ==========================================

  const grupos = useMemo(() => {
    const termo = search.trim().toLowerCase();

    const filtradas = turmas.filter((turma) => {
      const nomeNivel = getNivelNome(turma).toLowerCase();

      const nomeProfessor = getProfessorNome(turma).toLowerCase();
      const nomeSala = getSalaNome(turma).toLowerCase();
      const horario = getHorarioTexto(turma).toLowerCase();

      const id = String(turma.id);

      const correspondePesquisa =
        !termo ||
        nomeNivel.includes(termo) ||
        nomeProfessor.includes(termo) ||
        nomeSala.includes(termo) ||
        horario.includes(termo) ||
        id.includes(termo) ||
        (detalhes[turma.id]?.alunos || []).some((aluno) =>
          `${getEnrollmentName(aluno)} ${getStudentNumber(aluno)}`
            .toLowerCase()
            .includes(termo)
        );

      const correspondeNivel =
        !filtroNivel ||
        Number(turma.nivel_id) === Number(filtroNivel);

      const correspondeStatus =
        filtroStatus === "TODAS" ||
        turma.status === filtroStatus;

      return (
        correspondePesquisa &&
        correspondeNivel &&
        correspondeStatus
      );
    });

    const agrupados = {};

    filtradas.forEach((turma) => {
      const chave = turma.nivel_id || "sem-nivel";

      if (!agrupados[chave]) {
        agrupados[chave] = {
          id: chave,
          nome: getNivelNome(turma),
          tipo: turma.nivel_tipo || getNivel(turma.nivel_id)?.tipo,
          turmas: []
        };
      }

      agrupados[chave].turmas.push(turma);
    });

    return Object.values(agrupados).sort((a, b) =>
      a.nome.localeCompare(b.nome, "pt")
    );
  }, [
    turmas,
    search,
    filtroNivel,
    filtroStatus,
    niveis,
    salas,
    professores,
    horarios,
    detalhes
  ]);

  const totalAlunos = turmas.reduce(
    (total, turma) => total + getTotalAlunos(turma),
    0
  );

  const totalAtivas = turmas.filter(
    (turma) => turma.status === "ATIVA"
  ).length;

  const totalEncerradas = turmas.filter(
    (turma) => turma.status === "ENCERRADA"
  ).length;

  // ==========================================
  // DETALHES E ALUNOS DA TURMA
  // ==========================================

  const toggleTurma = async (id) => {
    const aberta = expanded.includes(id);

    if (aberta) {
      setExpanded((prev) => prev.filter((item) => item !== id));
      return;
    }

    setExpanded((prev) => [...prev, id]);

    if (detalhes[id]) return;

    setLoadingDetails((prev) => [...prev, id]);

    try {
      const response = await api.get(`/turmas/${id}`);

      setDetalhes((prev) => ({
        ...prev,
        [id]: response.data
      }));

    } catch (error) {
      console.error("Erro ao carregar alunos da turma:", error);
      notify("error", "Não foi possível carregar os alunos desta turma.");

    } finally {
      setLoadingDetails((prev) =>
        prev.filter((item) => item !== id)
      );
    }
  };

  // ==========================================
  // FORMULÁRIO
  // ==========================================

  const openCreate = () => {
    setEditId(null);
    setForm(initialForm);
    setStudentSearch("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditId(null);
    setForm(initialForm);
    setStudentSearch("");
  };

  const openEdit = async (turma) => {
    try {
      let dados = detalhes[turma.id];

      if (!dados) {
        const response = await api.get(`/turmas/${turma.id}`);
        dados = response.data;

        setDetalhes((prev) => ({
          ...prev,
          [turma.id]: dados
        }));
      }

      setEditId(turma.id);

      setForm({
        nivel_id: String(turma.nivel_id || ""),
        sala_id: String(turma.sala_id || ""),
        professor_id: turma.professor_id
          ? String(turma.professor_id)
          : "",
        horario_id: turma.horario_id
          ? String(turma.horario_id)
          : "",
        data_inicio: String(turma.data_inicio || "").slice(0, 10),
        data_fim: turma.data_fim
          ? String(turma.data_fim).slice(0, 10)
          : "",
        status: turma.status || "ATIVA",
        inscricoes_ids: (dados.alunos || []).map((aluno) =>
          Number(aluno.inscricao_id)
        )
      });

      setStudentSearch("");
      setModalOpen(true);

    } catch (error) {
      notify("error", getError(error, "Erro ao abrir a turma."));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,

      // Ao mudar de nível, limpar as inscrições seleccionadas
      ...(name === "nivel_id" ? { inscricoes_ids: [] } : {})
    }));
  };

  const toggleEnrollment = (id) => {
    setForm((prev) => {
      const existe = prev.inscricoes_ids.includes(Number(id));

      return {
        ...prev,
        inscricoes_ids: existe
          ? prev.inscricoes_ids.filter(
              (item) => item !== Number(id)
            )
          : [...prev.inscricoes_ids, Number(id)]
      };
    });
  };

  const inscricoesDisponiveis = useMemo(() => {
    return inscricoes.filter((item) => {
      const nivelId =
        item.nivel_id ||
        item.nivel?.id;

      if (
        form.nivel_id &&
        Number(nivelId) !== Number(form.nivel_id)
      ) {
        return false;
      }

      const pertenceTurmaActual = form.inscricoes_ids.includes(
        getEnrollmentId(item)
      );

      const termo = studentSearch.toLowerCase();

      const correspondePesquisa =
        !termo ||
        `${getEnrollmentName(item)} ${getStudentNumber(item)}`
          .toLowerCase()
          .includes(termo);

      // Se a inscrição já pertence a outra turma, não a disponibilizar.
      // As inscrições seleccionadas na turma actual continuam visíveis.
      const ocupadaPorOutraTurma = turmas.some((turma) => {
        const alunos = detalhes[turma.id]?.alunos || [];

        return (
          Number(turma.id) !== Number(editId) &&
          alunos.some(
            (aluno) =>
              Number(aluno.inscricao_id) === getEnrollmentId(item)
          )
        );
      });

      return (
        correspondePesquisa &&
        (pertenceTurmaActual || !ocupadaPorOutraTurma)
      );
    });
  }, [
    inscricoes,
    form.nivel_id,
    form.inscricoes_ids,
    studentSearch,
    turmas,
    detalhes,
    editId
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.nivel_id || !form.sala_id || !form.data_inicio) {
      notify("error", "Preencha o nível, a sala e a data de início.");
      return;
    }

    if (
      form.data_fim &&
      form.data_fim < form.data_inicio
    ) {
      notify("error", "A data de fim não pode ser anterior à data de início.");
      return;
    }

    setSaving(true);

    const payload = {
      nivel_id: Number(form.nivel_id),
      sala_id: Number(form.sala_id),
      professor_id: form.professor_id
        ? Number(form.professor_id)
        : null,
      horario_id: form.horario_id
        ? Number(form.horario_id)
        : null,
      data_inicio: form.data_inicio,
      data_fim: form.data_fim || null,
      status: form.status,
      inscricoes_ids: form.inscricoes_ids
    };

    try {
      if (editId) {
        await api.put(`/turmas/${editId}`, payload);
        notify("success", "Turma actualizada com sucesso.");
      } else {
        await api.post("/turmas", payload);
        notify("success", "Turma criada com sucesso.");
      }

      setModalOpen(false);
      setEditId(null);
      setForm(initialForm);

      await loadTurmas();

      // Limpar detalhes antigos para evitar dados desactualizados
      setDetalhes({});
      setExpanded([]);

    } catch (error) {
      console.error("Erro ao guardar turma:", error);
      notify("error", getError(error, "Não foi possível guardar a turma."));

    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // ELIMINAR TURMA
  // ==========================================

  const handleDelete = async (turma) => {
    const confirmar = window.confirm(
      `Tem a certeza de que pretende remover a Turma #${turma.id}?\n\n` +
      "A turma e as suas associações serão removidas. " +
      "Os alunos e as inscrições permanecerão no sistema."
    );

    if (!confirmar) return;

    try {
      await api.delete(`/turmas/${turma.id}`);

      setDetalhes((prev) => {
        const novo = { ...prev };
        delete novo[turma.id];
        return novo;
      });

      setExpanded((prev) =>
        prev.filter((id) => id !== turma.id)
      );

      await loadTurmas();

      notify("success", "Turma removida com sucesso.");

    } catch (error) {
      notify("error", getError(error, "Erro ao remover turma."));
    }
  };

  // ==========================================
  // RENDERIZAÇÃO
  // ==========================================

  return (
    <div className="turmas-page">

      {/* CABEÇALHO */}
      <div className="turmas-header">
        <div>
          <div className="turmas-eyebrow">
            <FaLayerGroup />
            GESTÃO ACADÉMICA
          </div>

          <h1>Gestão de Turmas</h1>

          <p>
            Organize as turmas, acompanhe os alunos e controle
            a distribuição académica.
          </p>
        </div>

        <button className="turmas-primary-btn" onClick={openCreate}>
          <FaPlus />
          <span>Nova turma</span>
        </button>
      </div>

      {/* NOTIFICAÇÕES */}
      {notice && (
        <div className={`turmas-notice ${notice.type}`}>
          {notice.type === "success"
            ? <FaCheckCircle />
            : <FaExclamationCircle />}
          {notice.message}
          <button onClick={() => setNotice(null)}>
            <FaTimes />
          </button>
        </div>
      )}

      {/* INDICADORES */}
      <div className="turmas-stats">

        <div className="turmas-stat-card">
          <div className="turmas-stat-icon purple">
            <FaLayerGroup />
          </div>
          <div>
            <span>Total de turmas</span>
            <strong>{turmas.length}</strong>
            <small>Registadas no sistema</small>
          </div>
        </div>

        <div className="turmas-stat-card">
          <div className="turmas-stat-icon blue">
            <FaUsers />
          </div>
          <div>
            <span>Alunos associados</span>
            <strong>{totalAlunos}</strong>
            <small>Nas turmas carregadas</small>
          </div>
        </div>

        <div className="turmas-stat-card">
          <div className="turmas-stat-icon red">
            <FaRegCalendarCheck />
          </div>
          <div>
            <span>Turmas activas</span>
            <strong>{totalAtivas}</strong>
            <small>Em funcionamento</small>
          </div>
        </div>

        <div className="turmas-stat-card">
          <div className="turmas-stat-icon gray">
            <FaCheckCircle />
          </div>
          <div>
            <span>Encerradas</span>
            <strong>{totalEncerradas}</strong>
            <small>Turmas finalizadas</small>
          </div>
        </div>

      </div>

      {/* FILTROS */}
      <div className="turmas-toolbar">
        <div className="turmas-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Pesquisar turma, professor, sala ou aluno..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {search && (
            <button onClick={() => setSearch("")}>
              <FaTimes />
            </button>
          )}
        </div>

        <div className="turmas-filters">
          <div className="turmas-filter-select">
            <FaGraduationCap />
            <select
              value={filtroNivel}
              onChange={(e) => setFiltroNivel(e.target.value)}
            >
              <option value="">Todos os níveis</option>
              {niveis.map((nivel) => (
                <option key={nivel.id} value={nivel.id}>
                  {nivel.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="turmas-filter-select">
            <FaFilter />
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
            >
              <option value="TODAS">Todos os estados</option>
              <option value="ATIVA">Activas</option>
              <option value="ENCERRADA">Encerradas</option>
            </select>
          </div>

          <button
            className="turmas-refresh-btn"
            onClick={loadDados}
            title="Actualizar dados"
          >
            <FaSyncAlt />
          </button>
        </div>
      </div>

      {/* LISTAGEM AGRUPADA */}
      {loading ? (
        <div className="turmas-loading">
          <FaSyncAlt className="turmas-spinner" />
          <span>A carregar turmas...</span>
        </div>
      ) : grupos.length === 0 ? (
        <div className="turmas-empty">
          <div className="turmas-empty-icon">
            <FaLayerGroup />
          </div>
          <h3>Nenhuma turma encontrada</h3>
          <p>
            Não existem turmas para os filtros seleccionados.
            Crie uma nova turma para começar.
          </p>
          <button className="turmas-primary-btn" onClick={openCreate}>
            <FaPlus />
            Criar primeira turma
          </button>
        </div>
      ) : (
        <div className="turmas-groups">

          {grupos.map((grupo) => (
            <section className="turmas-group" key={grupo.id}>

              {/* IDENTIFICAÇÃO DO NÍVEL */}
              <div className="turmas-group-heading">
                <div className="turmas-group-mark">
                  <FaGraduationCap />
                </div>

                <div className="turmas-group-title">
                  <span>GRUPO ACADÉMICO</span>
                  <h2>{grupo.nome}</h2>
                  <p>
                    {grupo.tipo || "Curso"} · {grupo.turmas.length}{" "}
                    {grupo.turmas.length === 1 ? "turma" : "turmas"}
                  </p>
                </div>

                <div className="turmas-group-count">
                  {grupo.turmas.length}
                  <span>TURMAS</span>
                </div>
              </div>

              {/* CARTÕES DAS TURMAS */}
              <div className="turmas-grid">

                {grupo.turmas.map((turma) => {
                  const aberta = expanded.includes(turma.id);
                  const dados = detalhes[turma.id];
                  const alunos = dados?.alunos || [];
                  const total = getTotalAlunos(turma);

                  return (
                    <article
                      className={`turma-card ${aberta ? "is-expanded" : ""}`}
                      key={turma.id}
                    >

                      <div className="turma-card-top">
                        <div className="turma-card-identity">
                          <div className="turma-card-symbol">
                            <FaBookOpen />
                          </div>

                          <div>
                            <span>IDENTIFICAÇÃO</span>
                            <h3>Turma #{String(turma.id).padStart(3, "0")}</h3>
                          </div>
                        </div>

                        <span
                          className={`turma-status ${
                            turma.status === "ATIVA" ? "active" : "closed"
                          }`}
                        >
                          <i />
                          {turma.status === "ATIVA" ? "Activa" : "Encerrada"}
                        </span>
                      </div>

                      <div className="turma-card-level">
                        <span>{getNivelNome(turma)}</span>
                        <small>
                          {turma.nivel_tipo ||
                            getNivel(turma.nivel_id)?.tipo ||
                            "CURSO"}
                        </small>
                      </div>

                      <div className="turma-card-details">

                        <div className="turma-detail">
                          <div className="turma-detail-icon teacher">
                            <FaChalkboardTeacher />
                          </div>
                          <div>
                            <small>Professor</small>
                            <strong>{getProfessorNome(turma)}</strong>
                          </div>
                        </div>

                        <div className="turma-detail">
                          <div className="turma-detail-icon room">
                            <FaDoorOpen />
                          </div>
                          <div>
                            <small>Sala</small>
                            <strong>{getSalaNome(turma)}</strong>
                          </div>
                        </div>

                        <div className="turma-detail">
                          <div className="turma-detail-icon time">
                            <FaClock />
                          </div>
                          <div>
                            <small>Horário</small>
                            <strong>{getHorarioTexto(turma)}</strong>
                          </div>
                        </div>

                        <div className="turma-detail">
                          <div className="turma-detail-icon date">
                            <FaCalendarAlt />
                          </div>
                          <div>
                            <small>Início / Fim</small>
                            <strong>
                              {formatDate(turma.data_inicio)}
                              {" — "}
                              {formatDate(turma.data_fim)}
                            </strong>
                          </div>
                        </div>

                      </div>

                      <div className="turma-card-students">
                        <div className="turma-student-avatars">
                          {dados ? (
                            alunos.slice(0, 4).map((aluno) => (
                              <div
                                className="turma-avatar"
                                key={aluno.inscricao_id}
                                title={getEnrollmentName(aluno)}
                              >
                                {getEnrollmentName(aluno)
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                            ))
                          ) : (
                            <div className="turma-avatar placeholder">
                              <FaUsers />
                            </div>
                          )}
                        </div>

                        <div className="turma-student-count">
                          <strong>{total}</strong>
                          <span>
                            {total === 1 ? "Aluno associado" : "Alunos associados"}
                          </span>
                        </div>

                        <button
                          className="turma-expand-btn"
                          onClick={() => toggleTurma(turma.id)}
                        >
                          {aberta ? "Fechar" : "Ver alunos"}
                          {aberta ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>

                      {/* PAINEL EXPANSÍVEL DOS ALUNOS */}
                      {aberta && (
                        <div className="turma-students-panel">

                          <div className="turma-panel-heading">
                            <div>
                              <h4>
                                <FaUserGraduate />
                                Alunos da turma
                              </h4>
                              <span>
                                Lista de inscrições associadas
                              </span>
                            </div>

                            <span className="turma-panel-total">
                              {total} alunos
                            </span>
                          </div>

                          {loadingDetails.includes(turma.id) ? (
                            <div className="turma-detail-loading">
                              <FaSyncAlt className="turmas-spinner" />
                              A carregar alunos...
                            </div>
                          ) : alunos.length === 0 ? (
                            <div className="turma-no-students">
                              <FaUsers />
                              <span>Esta turma ainda não tem alunos associados.</span>
                            </div>
                          ) : (
                            <div className="turma-students-list">
                              {alunos.map((aluno, index) => (
                                <div
                                  className="turma-student-row"
                                  key={aluno.inscricao_id || index}
                                >
                                  <div className="turma-student-avatar">
                                    {getEnrollmentName(aluno)
                                      .charAt(0)
                                      .toUpperCase()}
                                  </div>

                                  <div className="turma-student-info">
                                    <strong>{getEnrollmentName(aluno)}</strong>
                                    <span>
                                      Nº {getStudentNumber(aluno)}
                                    </span>
                                  </div>

                                  <div className="turma-student-contact">
                                    {aluno.telefone || "Sem contacto"}
                                  </div>

                                  <span className="turma-student-index">
                                    {String(index + 1).padStart(2, "0")}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ACÇÕES */}
                      <div className="turma-card-actions">
                        <button
                          className="turma-edit-action"
                          onClick={() => openEdit(turma)}
                        >
                          <FaEdit />
                          Editar turma
                        </button>

                        <button
                          className="turma-delete-action"
                          onClick={() => handleDelete(turma)}
                          title="Remover turma"
                        >
                          <FaTrash />
                        </button>
                      </div>

                    </article>
                  );
                })}

              </div>
            </section>
          ))}

        </div>
      )}

      {/* =====================================
          MODAL DE CRIAÇÃO / EDIÇÃO
      ===================================== */}

      {modalOpen && (
        <div
          className="turmas-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="turmas-modal">

            <div className="turmas-modal-header">
              <div>
                <span>GESTÃO ACADÉMICA</span>
                <h2>{editId ? "Editar turma" : "Criar nova turma"}</h2>
                <p>
                  Configure os dados da turma e associe os alunos.
                </p>
              </div>

              <button
                className="turmas-modal-close"
                onClick={closeModal}
                disabled={saving}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit}>

              <div className="turmas-form-section">
                <div className="turmas-form-section-title">
                  <span>01</span>
                  <div>
                    <h3>Informações académicas</h3>
                    <p>Defina o nível e a organização da turma.</p>
                  </div>
                </div>

                <div className="turmas-form-grid">

                  <label className="turmas-field">
                    <span>Nível académico *</span>
                    <select
                      name="nivel_id"
                      value={form.nivel_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Seleccionar nível</option>
                      {niveis.map((nivel) => (
                        <option key={nivel.id} value={nivel.id}>
                          {nivel.nome} {nivel.tipo ? `(${nivel.tipo})` : ""}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="turmas-field">
                    <span>Sala *</span>
                    <select
                      name="sala_id"
                      value={form.sala_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Seleccionar sala</option>
                      {salas.map((sala) => (
                        <option key={sala.id} value={sala.id}>
                          {sala.nome}
                          {sala.capacidade
                            ? ` · Capacidade ${sala.capacidade}`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="turmas-field">
                    <span>Professor</span>
                    <select
                      name="professor_id"
                      value={form.professor_id}
                      onChange={handleChange}
                    >
                      <option value="">Sem professor atribuído</option>
                      {professores.map((professor) => (
                        <option key={professor.id} value={professor.id}>
                          {professor.nome}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="turmas-field">
                    <span>Horário</span>
                    <select
                      name="horario_id"
                      value={form.horario_id}
                      onChange={handleChange}
                    >
                      <option value="">Seleccionar horário</option>
                      {horarios.map((horario) => (
                        <option key={horario.id} value={horario.id}>
                          {String(horario.hora_inicio).slice(0, 5)}
                          {" - "}
                          {String(horario.hora_fim).slice(0, 5)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="turmas-field">
                    <span>Data de início *</span>
                    <input
                      type="date"
                      name="data_inicio"
                      value={form.data_inicio}
                      onChange={handleChange}
                      required
                    />
                  </label>

                  <label className="turmas-field">
                    <span>Data de encerramento</span>
                    <input
                      type="date"
                      name="data_fim"
                      value={form.data_fim}
                      onChange={handleChange}
                    />
                  </label>

                  <label className="turmas-field">
                    <span>Estado da turma</span>
                    <select
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                    >
                      <option value="ATIVA">Activa</option>
                      <option value="ENCERRADA">Encerrada</option>
                    </select>
                  </label>

                </div>
              </div>

              <div className="turmas-form-section">
                <div className="turmas-form-section-title">
                  <span>02</span>
                  <div>
                    <h3>Associar alunos</h3>
                    <p>
                      Seleccione os alunos inscritos no nível escolhido.
                    </p>
                  </div>
                </div>

                <div className="turmas-selection-summary">
                  <div>
                    <FaUsers />
                    <span>Alunos seleccionados</span>
                  </div>
                  <strong>{form.inscricoes_ids.length}</strong>
                </div>

                {!form.nivel_id ? (
                  <div className="turmas-select-placeholder">
                    <FaGraduationCap />
                    <span>
                      Primeiro seleccione o nível académico para
                      visualizar as inscrições disponíveis.
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="turmas-student-search">
                      <FaSearch />
                      <input
                        type="text"
                        placeholder="Pesquisar aluno ou número de estudante..."
                        value={studentSearch}
                        onChange={(e) => setStudentSearch(e.target.value)}
                      />
                    </div>

                    <div className="turmas-enrollments-list">
                      {inscricoesDisponiveis.length === 0 ? (
                        <div className="turmas-no-enrollments">
                          Não existem inscrições disponíveis para este nível.
                        </div>
                      ) : (
                        inscricoesDisponiveis.map((item) => {
                          const id = getEnrollmentId(item);
                          const selected =
                            form.inscricoes_ids.includes(id);

                          return (
                            <label
                              className={`turmas-enrollment-item ${
                                selected ? "selected" : ""
                              }`}
                              key={id}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleEnrollment(id)}
                              />

                              <div className="turmas-enrollment-avatar">
                                {getEnrollmentName(item)
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>

                              <div className="turmas-enrollment-info">
                                <strong>{getEnrollmentName(item)}</strong>
                                <span>
                                  Nº {getStudentNumber(item)}
                                </span>
                              </div>

                              <span className="turmas-enrollment-check">
                                <FaCheckCircle />
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="turmas-modal-footer">
                <button
                  type="button"
                  className="turmas-cancel-btn"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="turmas-primary-btn"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <FaSyncAlt className="turmas-spinner" />
                      A guardar...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      {editId ? "Guardar alterações" : "Criar turma"}
                    </>
                  )}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default Turmas;
